import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { CheckCircle2, LifeBuoy, Loader2, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SupportSection() {
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Enter a valid email so we can reply.");
      return;
    }
    if (message.trim().length < 5) {
      setError("Tell us a little more about what you need.");
      return;
    }
    setError("");
    setState("loading");
    try {
      await axios.post(`${API}/support`, { email, message });
      setState("done");
    } catch {
      setState("idle");
      setError("Something went wrong — email us at support@fitnance.app instead.");
    }
  };

  return (
    <section id="support" className="border-t border-white/5 py-24 sm:py-32">
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-4 sm:px-6 lg:grid-cols-12 lg:gap-16 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-6"
        >
          <p className="mb-4 flex items-center gap-2 font-mono text-xs font-semibold uppercase tracking-[0.3em] text-emerald-400/90">
            <LifeBuoy className="h-4 w-4" />
            Support
          </p>
          <h2 className="text-2xl font-bold leading-[1.15] tracking-tight sm:text-4xl lg:text-5xl">
            Questions about the app? We answer fast.
          </h2>
          <p className="mt-4 max-w-xl text-base leading-relaxed text-neutral-300">
            Whether it's about your data, your subscription, or a bug that
            doesn't feel right — send it over. Every message lands directly
            with the founding team, not a bot queue.
          </p>
          <a
            data-testid="support-email-link"
            href="mailto:support@fitnance.app"
            className="mt-6 inline-block font-mono text-sm tracking-[0.15em] text-emerald-400 transition-colors duration-300 hover:text-emerald-300"
          >
            support@fitnance.app
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
          className="rounded-3xl border border-white/5 bg-[#111318] p-8 transition-colors duration-500 hover:border-emerald-500/30 lg:col-span-6"
        >
          {state === "done" ? (
            <div
              data-testid="support-success-message"
              className="flex items-center gap-3 rounded-2xl border border-emerald-500/30 bg-emerald-500/[0.08] px-5 py-4"
            >
              <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-400" />
              <p className="text-sm text-emerald-100">
                Message received — we'll get back to you within 24 hours.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="flex flex-col gap-4">
              <input
                data-testid="support-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email"
                className="h-12 w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 text-sm text-slate-100 placeholder:text-neutral-600 outline-none transition-colors duration-300 focus:border-emerald-500/50"
              />
              <textarea
                data-testid="support-message-input"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="How can we help?"
                rows={4}
                className="w-full resize-none rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm text-slate-100 placeholder:text-neutral-600 outline-none transition-colors duration-300 focus:border-emerald-500/50"
              />
              {error && (
                <p data-testid="support-form-error" className="text-xs text-red-400">
                  {error}
                </p>
              )}
              <button
                data-testid="support-submit-button"
                type="submit"
                disabled={state === "loading"}
                className="group flex h-12 items-center justify-center gap-2 rounded-full bg-emerald-500 font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-emerald-950 transition-all duration-300 hover:scale-[1.02] hover:bg-emerald-400 hover:shadow-[0_0_24px_rgba(16,185,129,0.4)] disabled:opacity-60"
              >
                {state === "loading" ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    Send message
                    <Send className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                  </>
                )}
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}
